import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button } from '@airia/ui';
import { RefreshCw, Copy, Check, Pencil, Eye, Mail, Reply } from 'lucide-react';
import { Textarea } from '../../ui/textarea';
import { stripHtml } from '../../lib/stripHtml';
import { copyTextToClipboard } from '../../lib/clipboard';
const DraftPreview = ({ content, subject, onRegenerate, onOpenInOutlook, isLoading = false, variant, error, }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState('');
    const [editedSubject, setEditedSubject] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isOpening, setIsOpening] = useState(false);
    // Initialize edited content when content changes
    useEffect(() => {
        const plainText = stripHtml(content);
        setEditedContent(plainText);
    }, [content]);
    // Initialize edited subject when subject changes
    useEffect(() => {
        setEditedSubject(subject || '');
    }, [subject]);
    // Auto-reset isCopied after 2 seconds
    useEffect(() => {
        let timeout;
        if (isCopied) {
            timeout = setTimeout(() => setIsCopied(false), 2000);
        }
        return () => clearTimeout(timeout);
    }, [isCopied]);
    const handleCopyToClipboard = async () => {
        try {
            await copyTextToClipboard(editedContent);
            setIsCopied(true);
        }
        catch (err) {
            console.error('Error copying to clipboard:', err);
        }
    };
    const handleOpenInOutlook = async () => {
        setIsOpening(true);
        try {
            await onOpenInOutlook(editedContent, editedSubject || undefined);
        }
        finally {
            setIsOpening(false);
        }
    };
    const toggleEditMode = () => {
        setIsEditing(!isEditing);
    };
    const ActionIcon = variant === 'reply' ? Reply : Mail;
    const actionLabel = variant === 'reply' ? 'Reply' : 'New Email';
    return (_jsxs("div", { className: "mt-4 space-y-3", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "text-xs font-medium text-muted-foreground", children: "AI Draft:" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: toggleEditMode, className: "h-7 px-2 text-xs", children: isEditing ? (_jsxs(_Fragment, { children: [_jsx(Eye, { className: "size-3 mr-1" }), "Preview"] })) : (_jsxs(_Fragment, { children: [_jsx(Pencil, { className: "size-3 mr-1" }), "Edit"] })) })] }), variant === 'new-email' && isEditing && (_jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "text-xs font-medium text-muted-foreground", children: "Subject:" }), _jsx("input", { type: "text", value: editedSubject, onChange: (e) => setEditedSubject(e.target.value), className: "w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20", placeholder: "Email subject" })] })), _jsx("div", { className: "p-3 bg-background border border-border rounded-md min-h-[180px] max-h-[400px] overflow-y-auto", children: isEditing ? (_jsx(Textarea, { value: editedContent, onChange: (e) => setEditedContent(e.target.value), className: "w-full text-sm resize-none border-0 p-0 focus-visible:ring-0 field-sizing-content", style: { minHeight: '156px', fieldSizing: 'content' }, placeholder: "Edit your draft here..." })) : (_jsx("div", { className: "text-sm text-foreground whitespace-pre-wrap", children: editedContent })) }), error && (_jsx("div", { className: "p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive", children: error })), _jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsxs(Button, { variant: "outline", size: "sm", onClick: onRegenerate, className: "w-full", disabled: isLoading, children: [_jsx(RefreshCw, { className: `size-4 mr-1 ${isLoading ? 'animate-spin' : ''}` }), "Regenerate"] }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleCopyToClipboard, className: "w-full", children: isCopied ? (_jsxs(_Fragment, { children: [_jsx(Check, { className: "size-4 mr-1" }), "Copied!"] })) : (_jsxs(_Fragment, { children: [_jsx(Copy, { className: "size-4 mr-1" }), "Copy"] })) })] }), _jsxs(Button, { variant: "default", size: "sm", onClick: handleOpenInOutlook, className: "w-full", disabled: isOpening || isLoading, children: [_jsx(ActionIcon, { className: "size-4 mr-2" }), isOpening ? 'Opening...' : actionLabel] })] }));
};
export default DraftPreview;
